import { EntityIconMap } from '@common/Entity/constants';
import { EntityIcon } from '@common/Entity/EntityIcons';
import { UserProfileDropdown } from '@common/UserProfileDropdown';
import { normalizeStringForTestId } from '@common/utils';
import { Breadcrumb, Button, ButtonProps, Stack, Text, theme } from '@prophecy/ui';
import { CrumbTypes } from '@prophecy/ui/Breadcrumb/types';
import styled from 'styled-components';

type Crumbs = {
  category?: string;
  label: string;
  url?: string;
  icon?: React.ReactNode;
}[];

const StyledBreadcrumbSeparator = styled(Breadcrumb.Icon)`
  color: ${theme.colors.gray400};
  height: ${theme.sizes.x16};
  width: ${theme.sizes.x16};
`;

const Root = styled(Stack)`
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.gray200};
`;

const StyleButton = styled(Button)`
  display: inline-flex;
  & > span {
    display: inline-flex;
    height: ${theme.spaces.x32};
  }
` as typeof Button;

const StyleIconContainer = styled(Stack)`
  flex-shrink: 0;
  width: ${theme.spaces.x32};
  height: ${theme.spaces.x32};
  border-radius: ${theme.radius.x4};
  background: ${theme.colors.grayBlue100};
`;

const ProjectBreadcrumbSeparator = styled(Breadcrumb.Icon)`
  color: ${theme.colors.gray400};
  height: ${theme.sizes.x12};
  width: ${theme.sizes.x12};
  margin: 0;
`;

export type PageHeaderProps = {
  /** The crumbs to display in the header */
  crumbs: Crumbs;
  /** The children to display in the header */
  children?: React.ReactNode;
  /** Whether to show the category in the header */
  showCategory?: boolean;
  /** The class name to apply to the header */
  className?: string;
  /** Whether to wrap the header in a container, defaults to false */
  noWrap?: boolean;
};

export function PageHeader({ crumbs, children, showCategory = true, className, noWrap = false }: PageHeaderProps) {
  const crumbsLength = crumbs.length;
  const content = (
    <>
      <Stack direction='horizontal' alignY='center' align='space-between' grow='1' gap={theme.spaces.x12}>
        <Stack direction='horizontal' alignY={showCategory ? 'end' : 'center'} gap={theme.spaces.x12}>
          {crumbs.map(({ category, label, url, icon }, index) => {
            const content = (
              <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8} key={index}>
                {icon ? (
                  icon
                ) : category ? (
                  <EntityIcon size='s' entity={category as keyof typeof EntityIconMap} />
                ) : null}
                <Stack align='space-between'>
                  {category && showCategory && (
                    <Text level='xxs' tone={theme.colors.gray500}>
                      {category}
                    </Text>
                  )}
                  <Text level='sm' weight={theme.fontWeight.medium} tone={theme.colors.gray700}>
                    {label}
                  </Text>
                </Stack>
                {index < crumbsLength - 1 && <StyledBreadcrumbSeparator />}
              </Stack>
            );

            return url ? (
              <Button
                key={url}
                variant='plain'
                elementType='link'
                to={url}
                data-test-id={`page-header-crumb-${normalizeStringForTestId(label)}-button`}>
                {content}
              </Button>
            ) : (
              content
            );
          })}
        </Stack>
        {children}
      </Stack>
      <UserProfileDropdown />
    </>
  );

  if (noWrap) {
    return content;
  }

  return (
    <Root
      padding={`${theme.spaces.x6} ${theme.spaces.x12}`}
      gap={theme.spaces.x2}
      align='center'
      width='100%'
      className={className}>
      <Stack direction='horizontal' alignY='center' gap={theme.spaces.x12}>
        {content}
      </Stack>
    </Root>
  );
}

export function ProjectHeader<T extends CrumbTypes>({
  crumbs,
  children
}: {
  crumbs: ButtonProps<T>[];
  children?: React.ReactNode;
}) {
  const crumbsLength = crumbs.length;
  return (
    <>
      <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8} align='space-between' width='100%'>
        <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8}>
          {crumbs.map(({ children, icon, ...rest }, index) => {
            const isLast = index < crumbsLength - 1;
            const color = isLast ? theme.colors.gray700 : theme.colors.gray500;

            return (
              <>
                {/* @ts-ignore FIXME: fix type of crumbs */}

                <StyleButton variant='plain' {...rest}>
                  <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8} key={index}>
                    {icon ? (
                      <StyleIconContainer
                        padding={theme.spaces.x4}
                        direction='horizontal'
                        alignY='center'
                        align='center'>
                        {icon}
                      </StyleIconContainer>
                    ) : null}

                    <Text level='xs' weight={theme.fontWeight.medium} tone={color}>
                      {children}
                    </Text>
                  </Stack>
                </StyleButton>
                {isLast && <ProjectBreadcrumbSeparator />}
              </>
            );
          })}
        </Stack>
        <UserProfileDropdown />
      </Stack>
      {children}
    </>
  );
}
